import { useState } from 'react'
import { submitFeedback } from '../../lib/session'
import { useToast } from '../common/Toast'

interface FeedbackFormProps {
  sessionId: string
  participantId: string
  displayName: string
  hasSubmitted: boolean
}

export default function FeedbackForm({ sessionId, participantId, displayName, hasSubmitted }: FeedbackFormProps) {
  const { addToast } = useToast()
  const [overallExperience, setOverallExperience] = useState('')
  const [workflowBuilt, setWorkflowBuilt] = useState('')
  const [improvements, setImprovements] = useState('')
  const [recommendRating, setRecommendRating] = useState(0)
  const [additionalFeedback, setAdditionalFeedback] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (hasSubmitted) {
    return (
      <div className="mx-3 mb-2 card p-4">
        <p className="mono-label text-teal">Feedback received</p>
        <p className="text-sm text-navy/70 mt-1">Thanks. It goes straight to the people building this.</p>
      </div>
    )
  }

  async function handleSubmit() {
    if (!recommendRating) {
      addToast('Pick a rating first.', 'error')
      return
    }
    setSubmitting(true)
    try {
      await submitFeedback(sessionId, participantId, displayName, {
        overallExperience,
        workflowBuilt,
        improvements,
        recommendRating,
        additionalFeedback,
      })
      addToast('Feedback sent. Thank you.', 'success')
    } catch {
      addToast('Failed to submit feedback. Please try again.', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-3 mb-2 card p-4 space-y-3">
      <h3 className="mono-label text-teal border-b-2 border-navy pb-2">Session feedback</h3>

      <label className="block">
        <span className="mono-label text-navy/70">How did the session go?</span>
        <textarea
          value={overallExperience}
          onChange={(e) => setOverallExperience(e.target.value)}
          rows={2}
          className="mt-1.5 w-full border-2 border-navy/55 focus:border-navy bg-paper px-3 py-2 text-sm outline-none resize-none"
        />
      </label>

      <label className="block">
        <span className="mono-label text-navy/70">What workflow did you build?</span>
        <textarea
          value={workflowBuilt}
          onChange={(e) => setWorkflowBuilt(e.target.value)}
          rows={2}
          className="mt-1.5 w-full border-2 border-navy/55 focus:border-navy bg-paper px-3 py-2 text-sm outline-none resize-none"
        />
      </label>

      <label className="block">
        <span className="mono-label text-navy/70">What improvements would you suggest?</span>
        <textarea
          value={improvements}
          onChange={(e) => setImprovements(e.target.value)}
          rows={2}
          className="mt-1.5 w-full border-2 border-navy/55 focus:border-navy bg-paper px-3 py-2 text-sm outline-none resize-none"
        />
      </label>

      <div>
        <span className="mono-label text-navy/70">Would you recommend this? (1 to 5)</span>
        <div className="flex gap-2 mt-1.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRecommendRating(n)}
              aria-pressed={recommendRating === n}
              className={`w-10 h-10 border-2 font-mono text-sm font-semibold transition-colors cursor-pointer ${
                recommendRating === n
                  ? 'bg-amber border-navy text-white'
                  : 'bg-paper border-navy/25 text-navy/70 hover:border-navy'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <label className="block">
        <span className="mono-label text-navy/70">Anything else?</span>
        <textarea
          value={additionalFeedback}
          onChange={(e) => setAdditionalFeedback(e.target.value)}
          rows={2}
          className="mt-1.5 w-full border-2 border-navy/55 focus:border-navy bg-paper px-3 py-2 text-sm outline-none resize-none"
        />
      </label>

      <button
        onClick={handleSubmit}
        disabled={submitting}
        className="btn btn-navy w-full"
      >
        {submitting ? 'Sending...' : 'Send feedback'}
      </button>
    </div>
  )
}
